import { useEffect } from 'react';

interface OrganizationSchema {
  name: string;
  url: string;
  logo: string;
  description: string;
  address?: {
    addressCountry: string;
    addressLocality?: string;
  };
  contactPoint?: {
    contactType: string;
    email?: string;
    telephone?: string;
  };
}

interface ArticleSchema {
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified?: string;
  author: {
    name: string;
  };
  publisher: {
    name: string;
    logo: string;
  };
}

interface BreadcrumbSchema {
  items: Array<{
    name: string;
    url: string;
  }>;
}

interface StructuredDataProps {
  type: 'Organization' | 'Article' | 'BreadcrumbList';
  data: OrganizationSchema | ArticleSchema | BreadcrumbSchema;
}

export const StructuredData = ({ type, data }: StructuredDataProps) => {
  useEffect(() => {
    const scriptId = `structured-data-${type}`;
    
    // Remove existing script if any
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    // Create structured data based on type
    let structuredData: any = {
      '@context': 'https://schema.org',
      '@type': type,
    };

    if (type === 'Organization') {
      const orgData = data as OrganizationSchema;
      structuredData = {
        ...structuredData,
        name: orgData.name,
        url: orgData.url,
        logo: orgData.logo,
        description: orgData.description,
        ...(orgData.address && {
          address: {
            '@type': 'PostalAddress',
            addressCountry: orgData.address.addressCountry,
            ...(orgData.address.addressLocality && {
              addressLocality: orgData.address.addressLocality,
            }),
          },
        }),
        ...(orgData.contactPoint && {
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: orgData.contactPoint.contactType,
            ...(orgData.contactPoint.email && { email: orgData.contactPoint.email }),
            ...(orgData.contactPoint.telephone && { telephone: orgData.contactPoint.telephone }),
          },
        }),
      };
    } else if (type === 'Article') {
      const articleData = data as ArticleSchema;
      structuredData = {
        ...structuredData,
        headline: articleData.headline,
        description: articleData.description,
        image: articleData.image,
        datePublished: articleData.datePublished,
        ...(articleData.dateModified && { dateModified: articleData.dateModified }),
        author: {
          '@type': 'Person',
          name: articleData.author.name,
        },
        publisher: {
          '@type': 'Organization',
          name: articleData.publisher.name,
          logo: {
            '@type': 'ImageObject',
            url: articleData.publisher.logo,
          },
        },
      };
    } else if (type === 'BreadcrumbList') {
      const breadcrumbData = data as BreadcrumbSchema;
      structuredData = {
        ...structuredData,
        itemListElement: breadcrumbData.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      };
    }

    // Create and append script tag
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, data]);

  return null;
};
